import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Radar, Shield, FileText, Scale, HelpCircle, ShieldCheck, ChevronRight } from 'lucide-react';
import { useProximity } from '../context/ProximityContext';
import ProximityEnableModal from '../components/ProximityEnableModal';
import useTheme from '../hooks/useTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [settings, setSettings] = useState(null);
  const [showProximityModal, setShowProximityModal] = useState(false);
  
  const {
    proximityEnabled,
    visibilityMode,
    permissionStatus,
    enableProximity,
    disableProximity
  } = useProximity();

  // Theme-aware colors
  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#F1E6D0' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#6D6358',
    textAccent: isDark ? '#C8A878' : '#8B6914',
    border: isDark ? 'rgba(165, 124, 51, 0.3)' : 'rgba(139, 105, 20, 0.2)',
    sectionTitle: isDark ? '#C8A878' : '#8B6914',
    menuItemBg: isDark ? 'rgba(58, 42, 30, 1)' : 'rgba(253, 252, 250, 1)',
    infoBorder: isDark ? '#C8A878' : 'rgba(139, 105, 20, 0.3)',
    infoBg: isDark ? '#3A2A1E' : 'rgba(139, 105, 20, 0.08)'
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleToggle = async (name) => {
    const updatedSettings = { ...settings, [name]: !settings[name] };
    setSettings(updatedSettings);
    try {
      await axios.put(`${API}/settings`, updatedSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleProximityToggle = async () => {
    if (proximityEnabled) {
      await disableProximity();
    } else {
      setShowProximityModal(true);
    }
  };

  if (!settings) return <div style={{ padding: '20px', color: colors.text }}>Loading...</div>;

  const settingsSections = [
    {
      title: 'Privacy',
      items: [
        { key: 'show_age', label: 'Show Age', testId: 'show-age-toggle' },
        { key: 'show_distance', label: 'Show Distance', testId: 'show-distance-toggle' },
        { key: 'show_online', label: 'Show Online Status', testId: 'show-online-toggle' },
      ]
    },
    {
      title: 'Notifications',
      items: [
        { key: 'notifications_matches', label: 'New Matches', testId: 'notify-matches-toggle' },
        { key: 'notifications_messages', label: 'New Messages', testId: 'notify-messages-toggle' },
        { key: 'notifications_tables', label: 'Table Invites', testId: 'notify-tables-toggle' },
        { key: 'notifications_push', label: 'Push Notifications', testId: 'notify-push-toggle' },
      ]
    },
    {
      title: 'Location',
      items: [
        { key: 'allow_location', label: 'Allow Location Access', testId: 'allow-location-toggle' },
      ]
    },
  ];

  return (
    <div style={{ background: colors.background, minHeight: '100vh' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: isDark ? 'var(--bg-secondary)' : '#F5F2ED' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textAccent, padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="page-title">Settings</h1>
      </div>

      <div style={{ background: colors.background }}>
        {/* Proximity Discovery Section */}
        <div>
          <div style={{ padding: '16px 20px 8px', fontSize: '12px', fontWeight: '600', color: '#4CAF50', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Radar size={14} />
            Proximity Discovery
          </div>
          
          <div
            className="menu-item"
            style={{ padding: '16px 20px', background: isDark ? 'transparent' : 'transparent' }}
            data-testid="proximity-discovery-toggle"
          >
            <div>
              <span style={{ display: 'block', color: colors.text }}>Proximity Discovery</span>
              <span style={{ fontSize: '12px', color: colors.textMuted, display: 'block', marginTop: '4px' }}>
                Allow participating members within 60 feet to discover you
              </span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={proximityEnabled}
                onChange={handleProximityToggle}
              />
              <span className="toggle-slider" style={proximityEnabled ? { background: '#4CAF50' } : {}}></span>
            </label>
          </div>
          
          {proximityEnabled && (
            <div style={{ padding: '0 20px 16px' }}>
              <div style={{ 
                background: 'rgba(76, 175, 80, 0.1)', 
                border: '1px solid rgba(76, 175, 80, 0.3)',
                borderRadius: '10px', 
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Shield size={18} color="#4CAF50" />
                <p style={{ fontSize: '12px', color: '#4CAF50', margin: 0, lineHeight: '1.5' }}>
                  Your exact location is never shown to other members.
                </p>
              </div>
            </div>
          )}
          
          {permissionStatus === 'denied' && (
            <div style={{ padding: '0 20px 16px' }}>
              <div style={{ 
                background: 'rgba(244, 67, 54, 0.1)', 
                border: '1px solid rgba(244, 67, 54, 0.3)',
                borderRadius: '10px', 
                padding: '12px'
              }}>
                <p style={{ fontSize: '12px', color: '#F44336', margin: 0 }}>
                  Location permission is required. Please enable it in your browser settings.
                </p>
              </div>
            </div>
          )}
        </div>

        {settingsSections.map((section) => (
          <div key={section.title}>
            <div style={{ padding: '16px 20px 8px', fontSize: '12px', fontWeight: '600', color: colors.sectionTitle, textTransform: 'uppercase' }}>
              {section.title}
            </div>
            {section.items.map((item) => (
              <div
                key={item.key}
                className="menu-item"
                style={{ padding: '16px 20px', background: isDark ? 'transparent' : 'transparent' }}
                data-testid={item.testId}
              >
                <span style={{ color: colors.text }}>{item.label}</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings[item.key]}
                    onChange={() => handleToggle(item.key)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
          </div>
        ))}

        <div style={{ padding: '20px' }}>
          <div style={{ background: colors.infoBg, borderRadius: '12px', padding: '16px', border: `1px solid ${colors.infoBorder}` }}>
            <p style={{ fontSize: '14px', color: colors.text, lineHeight: '1.6' }}>
              <strong style={{ color: colors.textAccent }}>Privacy Zones:</strong> Hide your exact location when you're in specific areas. Manage them from your{' '}
              <span
                onClick={() => navigate('/profile/privacy-zones')}
                style={{ color: colors.textAccent, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Privacy Zones page
              </span>
              .
            </p>
          </div>
        </div>

        {/* Legal & Support Links */}
        <div>
          <div style={{ padding: '16px 20px 8px', fontSize: '12px', fontWeight: '600', color: colors.sectionTitle, textTransform: 'uppercase' }}>
            Legal & Support
          </div>
          
          <button
            onClick={() => navigate('/privacy')}
            className="menu-item"
            style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: colors.text }}
            data-testid="privacy-policy-link"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FileText size={20} color={colors.textAccent} />
              <span>Privacy Policy</span>
            </div>
            <ChevronRight size={20} color={colors.textMuted} />
          </button>
          
          <button
            onClick={() => navigate('/terms')}
            className="menu-item"
            style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: colors.text }}
            data-testid="terms-of-service-link"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Scale size={20} color={colors.textAccent} />
              <span>Terms of Service</span>
            </div>
            <ChevronRight size={20} color={colors.textMuted} />
          </button>
          
          <button
            onClick={() => navigate('/support')}
            className="menu-item"
            style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: colors.text }}
            data-testid="support-link"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <HelpCircle size={20} color={colors.textAccent} />
              <span>Help & Support</span>
            </div>
            <ChevronRight size={20} color={colors.textMuted} />
          </button>
          
          <button
            onClick={() => navigate('/safety')}
            className="menu-item"
            style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: colors.text }}
            data-testid="safety-link"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <ShieldCheck size={20} color={colors.textAccent} />
              <span>Community & Safety</span>
            </div>
            <ChevronRight size={20} color={colors.textMuted} />
          </button>
        </div>
      </div>

      {/* Proximity Enable Modal */}
      <ProximityEnableModal
        isOpen={showProximityModal}
        onClose={() => setShowProximityModal(false)}
        onSuccess={() => setShowProximityModal(false)}
      />
    </div>
  );
};

export default SettingsPage;
